import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import RecipeList from './RecipeList';
import ViewRecipe from './ViewRecipe';
import FormulationOrderList from './FormulationOrderList';
import ViewFormulationOrder from './ViewFormulationOrder';
import FormulationHistory from './FormulationHistory';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import '../Admin/Users.css';

const FormulationsManager = () => {
  const { t } = useTranslation('warehouse');
  const [activeSection, setActiveSection] = useState('recipes'); // 'recipes' | 'orders' | 'history'
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'view' | 'edit' | 'create'
  const [currentStatus, setCurrentStatus] = useState(null);
  const listRef = useRef();
  const viewRef = useRef();

  const handleSelect = (id) => {
    setSelectedId(id);
    setViewMode('view');
  };

  const handleBack = () => {
    setSelectedId(null);
    setViewMode('list');
    setCurrentStatus(null);
  };

  const handleNew = () => {
    setSelectedId(null);
    setViewMode('create');
    setCurrentStatus(null);
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleSave = () => {
    if (viewRef.current?.handleSave) {
      viewRef.current.handleSave();
    }
  };

  const handleDelete = () => {
    if (viewRef.current?.handleDelete) {
      viewRef.current.handleDelete();
    }
  };

  const handleCreated = (id) => {
    setSelectedId(id);
    setViewMode('view');
    if (listRef.current?.refresh) {
      listRef.current.refresh();
    }
  };

  const handleSaveComplete = () => {
    setViewMode('view');
    if (listRef.current?.refresh) {
      listRef.current.refresh();
    }
  };

  const handleRefresh = () => {
    if (listRef.current?.refresh) {
      listRef.current.refresh();
    }
  };

  const handleStatusLoaded = (status) => {
    setCurrentStatus(status);
  };

  // Reset selection when switching sections
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setSelectedId(null);
    setViewMode('list');
    setCurrentStatus(null);
  };

  const renderToolbar = () => {
    if (activeSection === 'history') {
      if (viewMode === 'view' && selectedId) {
        return (
          <button className="toolbar-btn" onClick={handleBack}>
            <span className="icon">⬅️</span> {t('common:ui.back')}
          </button>
        );
      }
      return null;
    }
    if (viewMode === 'list') {
      return (
        <button className="btn-primary" onClick={handleNew}>
          <span className="icon">+</span> {activeSection === 'recipes' ? t('formulationsManager.newRecipe') : t('formulationsManager.newOrder')}
        </button>
      );
    }
    if (viewMode === 'create' || viewMode === 'edit') {
      return (
        <>
          <button className="toolbar-btn primary" onClick={handleSave}>
            <span className="icon">💾</span> {t('common:ui.save')}
          </button>
          <button className="toolbar-btn" onClick={handleCancel}>
            <span className="icon">✖</span> {t('common:ui.cancel')}
          </button>
        </>
      );
    }
    // View mode
    return (
      <>
        {activeSection === 'recipes' && currentStatus !== 'OBSOLETE' && (
          <button className="toolbar-btn" onClick={handleEdit}>
            <span className="icon">✏️</span> {t('common:ui.edit')}
          </button>
        )}
        {activeSection === 'recipes' && (
          <button className="toolbar-btn danger" onClick={handleDelete}>
            <span className="icon">🗑️</span> {t('common:ui.delete')}
          </button>
        )}
        <button className="toolbar-btn" onClick={handleBack}>
          <span className="icon">⬅️</span> {t('common:ui.back')}
        </button>
      </>
    );
  };

  const renderRecipesPanel = () => (
    <ResizablePanels
      leftPanel={
        <RecipeList
          ref={listRef}
          onSelectRecipe={handleSelect}
          selectedRecipeId={selectedId}
          onNew={handleNew}
        />
      }
      rightPanel={
        viewMode !== 'list' ? (
          <ViewRecipe
            ref={viewRef}
            recipeId={selectedId}
            editMode={viewMode === 'edit'}
            createMode={viewMode === 'create'}
            onBack={handleBack}
            onEdit={handleEdit}
            onSave={handleSaveComplete}
            onCancel={handleCancel}
            onRecipeCreated={handleCreated}
            onStatusLoaded={handleStatusLoaded}
          />
        ) : null
      }
      leftCollapsed={viewMode !== 'list'}
      defaultLeftWidth={40}
      minLeftWidth={30}
      maxLeftWidth={70}
    />
  );

  const renderOrdersPanel = () => (
    <ResizablePanels
      leftPanel={
        <FormulationOrderList
          ref={listRef}
          onSelectOrder={handleSelect}
          selectedOrderId={selectedId}
          onNew={handleNew}
        />
      }
      rightPanel={
        viewMode !== 'list' ? (
          <ViewFormulationOrder
            ref={viewRef}
            orderId={selectedId}
            editMode={viewMode === 'edit'}
            createMode={viewMode === 'create'}
            onBack={handleBack}
            onSave={handleSaveComplete}
            onCancel={handleCancel}
            onOrderCreated={handleCreated}
            onStatusLoaded={handleStatusLoaded}
          />
        ) : null
      }
      leftCollapsed={viewMode !== 'list'}
      defaultLeftWidth={40}
      minLeftWidth={30}
      maxLeftWidth={70}
    />
  );

  const renderHistoryPanel = () => (
    <ResizablePanels
      leftPanel={
        <FormulationHistory
          selectedId={selectedId}
          onSelect={(id) => { setSelectedId(id); setViewMode('view'); }}
        />
      }
      rightPanel={
        viewMode === 'view' && selectedId ? (
          <ViewFormulationOrder
            orderId={selectedId}
            onBack={handleBack}
          />
        ) : null
      }
      leftCollapsed={viewMode === 'view' && !!selectedId}
      defaultLeftWidth={40}
      minLeftWidth={30}
      maxLeftWidth={70}
    />
  );

  return (
    <div className="departments-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '0 16px' }}>
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-secondary)', borderRadius: '6px', padding: '2px' }}>
          <button
            onClick={() => handleSectionChange('recipes')}
            style={{
              padding: '6px 16px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeSection === 'recipes' ? '600' : '400',
              background: activeSection === 'recipes' ? 'var(--bg-primary)' : 'transparent',
              color: activeSection === 'recipes' ? 'var(--text-primary)' : 'var(--text-secondary)',
              boxShadow: activeSection === 'recipes' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            🧪 {t('formulationsManager.tabs.recipes')}
          </button>
          <button
            onClick={() => handleSectionChange('orders')}
            style={{
              padding: '6px 16px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeSection === 'orders' ? '600' : '400',
              background: activeSection === 'orders' ? 'var(--bg-primary)' : 'transparent',
              color: activeSection === 'orders' ? 'var(--text-primary)' : 'var(--text-secondary)',
              boxShadow: activeSection === 'orders' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            📋 {t('formulationsManager.tabs.orders')}
          </button>
          <button
            onClick={() => handleSectionChange('history')}
            style={{
              padding: '6px 16px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeSection === 'history' ? '600' : '400',
              background: activeSection === 'history' ? 'var(--bg-primary)' : 'transparent',
              color: activeSection === 'history' ? 'var(--text-primary)' : 'var(--text-secondary)',
              boxShadow: activeSection === 'history' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            🕐 {t('formulationsManager.tabs.history')}
          </button>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          {renderToolbar()}
        </div>
      </div>

      {activeSection === 'recipes' && renderRecipesPanel()}
      {activeSection === 'orders' && renderOrdersPanel()}
      {activeSection === 'history' && renderHistoryPanel()}
    </div>
  );
};

export default FormulationsManager;
